"use client"

import { useState, useEffect } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Search, Send, Paperclip, Calendar, MapPin, X, AlertCircle, Mail, ArrowLeft, MessageCircle, Info, CheckCircle } from "lucide-react"
import { format } from "date-fns"
import { es } from "date-fns/locale"
import { Badge } from "../badge"
import { Input } from "../Input"
import { Button } from "../Button"
import { Textarea } from "../texttarea"
import { Avatar, AvatarFallback, AvatarImage } from "../Avatar"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../Tooltip"
import { URL_API } from "../../../config/default"
import { myHeaders } from "../../../config/axios"
import { CustomToast } from "../../../utils/custom-toast"
import { useAppContext } from "../../../contexts/AppProvider"

// API Functions for Chat
const chatAPI = {
    // Conversations
    createConversation: async (data: { booking_id: number; guest_id: number; host_id: number }) => {
        const response = await fetch(`${URL_API}/chat/conversations`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': myHeaders,
            },
            body: JSON.stringify(data),
        });
        return response.json();
    },

    getConversations: async () => {
        const response = await fetch(`${URL_API}/chat/conversations`, {
            headers: {
                'Authorization': myHeaders,
            },
        });
        return response.json();
    },

    getConversation: async (id: string) => {
        const response = await fetch(`${URL_API}/chat/conversations/${id}`, {
            headers: {
                'Authorization': myHeaders,
            },
        });
        return response.json();
    },

    updateConversation: async (id: string, data: { status: string }) => {
        const response = await fetch(`${URL_API}/chat/conversations/${id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': myHeaders,
            },
            body: JSON.stringify(data),
        });
        return response.json();
    },

    deleteConversation: async (id: string) => {
        const response = await fetch(`${URL_API}/chat/conversations/${id}`, {
            method: 'DELETE',
            headers: {
                'Authorization': myHeaders,
            },
        });
        return response.json();
    },

    // Messages
    sendMessage: async (data: { conversation_id: number; content: string }) => {
        const response = await fetch(`${URL_API}/chat/messages`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': myHeaders,
            },
            body: JSON.stringify(data),
        });
        return response.json();
    },

    getMessages: async (conversationId: string, page = 1, limit = 20) => {
        const response = await fetch(`${URL_API}/chat/conversations/${conversationId}/messages?page=${page}&limit=${limit}`, {
            headers: {
                'Authorization': myHeaders,
            },
        });
        return response.json();
    },

    getMessage: async (id: string) => {
        const response = await fetch(`${URL_API}/chat/messages/${id}`, {
            headers: {
                'Authorization': myHeaders,
            },
        });
        return response.json();
    },

    deleteMessage: async (id: string) => {
        const response = await fetch(`${URL_API}/chat/messages/${id}`, {
            method: 'DELETE',
            headers: {
                'Authorization': myHeaders,
            },
        });
        return response.json();
    },
};

interface Conversation {
    id: number;
    booking_id: number;
    guest_id: number;
    host_id: number;
    status?: string;
    booking_status?: string;
    created_at: string;
    updated_at: string;
    guest_email?: string;
    guest_firstname?: string;
    guest_lastname?: string;
    guest_phone?: string;
    host_email?: string;
    host_firstname?: string;
    host_lastname?: string;
    host_phone?: string;
    property_id?: number;
    excursion_id?: number;
    start_date?: string;
    end_date?: string;
}

interface Message {
    id: number;
    conversation_id: number;
    sender_id: number;
    content: string;
    created_at: string;
    firstname?: string;
    lastname?: string;
    image?: string;
}

interface ClientMessagesInterfaceProps {
    conversationId?: string;
}

export default function ClientMessagesInterface({ conversationId }: ClientMessagesInterfaceProps) {
    const { me }: any = useAppContext();
    const queryClient = useQueryClient();
    const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null)
    const [newMessage, setNewMessage] = useState("")
    const [searchTerm, setSearchTerm] = useState("")
    const [currentUserId, setCurrentUserId] = useState<number | null>(null)
    const [showEmailModal, setShowEmailModal] = useState(false)
    const [motivo, setMotivo] = useState("")
    const [sendingEmail, setSendingEmail] = useState(false)
    const [isMobile, setIsMobile] = useState(false)
    const [showConversation, setShowConversation] = useState(false)

    // Get current user ID from localStorage token
    useEffect(() => {
        const token = localStorage.getItem('token');

        if (token && me) {
            try {
                setCurrentUserId(me?.id);
            } catch (error) {
                console.error('Error parsing token:', error);
            }
        }
    }, [me]);

    // Detectar tamaño de pantalla para responsividad
    useEffect(() => {
        const checkScreenSize = () => {
            setIsMobile(window.innerWidth < 1024); // lg breakpoint
        };

        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);

        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    // Fetch conversations
    const { data: conversationsData, isLoading: conversationsLoading } = useQuery({
        queryKey: ['conversations'],
        queryFn: chatAPI.getConversations,
        enabled: !!localStorage.getItem('token'),
    });

    // Check for conversationId in path or booking_id in URL and find corresponding conversation
    useEffect(() => {
        if (conversationsData?.success) {
            // Prioridad 1: conversationId desde el path
            if (conversationId) {
                const conversation = conversationsData.data.find((conv: Conversation) =>
                    conv.id.toString() === conversationId
                );
                if (conversation) {
                    setSelectedConversation(conversation);
                    if (isMobile) {
                        setShowConversation(true);
                    }
                    return;
                }
            }

            // Prioridad 2: booking_id desde query params
            const urlParams = new URLSearchParams(window.location.search);
            const bookingId = urlParams.get('booking_id');

            if (bookingId) {
                const conversation = conversationsData.data.find((conv: Conversation) =>
                    conv.booking_id.toString() === bookingId
                );
                if (conversation) {
                    setSelectedConversation(conversation);
                    if (isMobile) {
                        setShowConversation(true);
                    }
                }
            }
        }
    }, [conversationsData, isMobile, conversationId]);

    // Fetch messages for selected conversation
    const { data: messagesData, isLoading: messagesLoading } = useQuery({
        queryKey: ['messages', selectedConversation?.id],
        queryFn: () => chatAPI.getMessages(selectedConversation!.id.toString()),
        enabled: !!selectedConversation,
    });

    // Send message mutation
    const sendMessageMutation = useMutation({
        mutationFn: chatAPI.sendMessage,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['messages', selectedConversation?.id] });
            queryClient.invalidateQueries({ queryKey: ['conversations'] });
            setNewMessage("");
            CustomToast({
                status: 200,
                msg: "",
                title: "Mensaje enviado correctamente",
            });
        },
        onError: (error: any) => {
            CustomToast({
                status: 400,
                msg: "",
                title: "Error al enviar el mensaje",
            });
            console.error('Error sending message:', error);
        },
    });

    // Close conversation mutation (only for hosts)
    const closeConversationMutation = useMutation({
        mutationFn: (conversationId: string) => chatAPI.updateConversation(conversationId, { status: 'closed' }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['conversations'] });
            CustomToast({
                status: 200,
                msg: "",
                title: "Conversación cerrada correctamente",
            });
        },
        onError: (error: any) => {
            CustomToast({
                status: 400,
                msg: "",
                title: "Error al cerrar la conversación",
            });
            console.error('Error closing conversation:', error);
        },
    });

    // Función para enviar correo de reserva
    const handleSendReservationEmail = async () => {
        if (!selectedConversation || !motivo.trim()) {
            CustomToast({
                status: 400,
                msg: "",
                title: "Por favor ingresa un motivo para el correo",
            });
            return;
        }

        setSendingEmail(true);

        try {
            // Buscar información del booking basado en la conversación
            const booking = {
                id: selectedConversation.booking_id,
                property: [{
                    owner_id: selectedConversation.host_id,
                    title: selectedConversation.property_id ? `Propiedad #${selectedConversation.property_id}` :
                        selectedConversation.excursion_id ? `Excursión #${selectedConversation.excursion_id}` : 'Reserva'
                }],
                customer: [{
                    email: selectedConversation?.guest_email || '',
                    phone: selectedConversation?.guest_phone || ''
                }]
            };

            let formdata = new FormData();
            formdata.append("status", "pending");
            formdata.append("motivo", motivo);
            formdata.append("owner_id", booking.property[0].owner_id.toString());
            formdata.append("property", booking.property[0].title);
            formdata.append("customer_email", booking.customer[0].email);
            formdata.append("phone", booking.customer[0].phone);

            const requestOptions = {
                method: "POST",
                body: formdata,
                headers: {
                    Authorization: myHeaders,
                },
            };

            const response = await fetch(`${URL_API}/reservations/${booking.id}`, requestOptions);
            const result = await response.json();

            if (response.ok) {
                CustomToast({
                    status: 200,
                    msg: "",
                    title: "Correo enviado correctamente. Se enviará un mail para que realice el pago de reserva",
                });

                queryClient.invalidateQueries({
                    queryKey: ["conversations"],
                });

                setShowEmailModal(false);
                setMotivo("");
            } else {
                throw new Error(result.message || 'Error al enviar el correo');
            }
        } catch (error: any) {
            console.error(error);
            CustomToast({
                status: 400,
                msg: "",
                title: "Error al enviar el correo",
            });
        } finally {
            setSendingEmail(false);
        }
    };

    const conversations = conversationsData?.success ? conversationsData.data : [];
    const messages = messagesData?.success ? messagesData.data.messages : [];

    const filteredConversations = conversations.filter((conv: Conversation) => {
        const hostName = `${conv.host_firstname || ''} ${conv.host_lastname || ''}`.toLowerCase();
        return hostName.includes(searchTerm.toLowerCase());
    });

    const handleSendMessage = () => {
        if (newMessage.trim() && selectedConversation && canSendMessage()) {
            sendMessageMutation.mutate({
                conversation_id: selectedConversation.id,
                content: newMessage.trim(),
            });
        }
    };

    const handleCloseConversation = () => {
        if (selectedConversation && isCurrentUserHost()) {
            closeConversationMutation.mutate(selectedConversation.id.toString());
        }
    };

    // Check if current user is the host
    const isCurrentUserHost = () => {
        return selectedConversation && currentUserId === selectedConversation.host_id;
    };

    // Check if current user is the guest
    const isCurrentUserGuest = () => {
        return selectedConversation && currentUserId === selectedConversation.guest_id;
    };

    // Check if conversation is closed
    const isConversationClosed = () => {
        return selectedConversation?.status === 'closed';
    };

    // Check if host has replied to the first message
    const hasHostReplied = () => {
        if (!selectedConversation || messages.length === 0) return false;

        // Check if there's any message from the host
        const hostMessages = messages.filter((msg: Message) => msg.sender_id === selectedConversation.host_id);
        return hostMessages.length > 0;
    };

    // Check if guest can send messages
    const canGuestSendMessage = () => {
        if (!isCurrentUserGuest()) return true;

        // If it's the guest's first message, allow it
        if (messages.length === 0) return true;

        // If host hasn't replied yet, check if guest has already sent a message
        if (!hasHostReplied()) {
            const guestMessages = messages.filter((msg: Message) => msg.sender_id === selectedConversation?.guest_id);
            return guestMessages.length === 0; // Only allow first message
        }

        return true; // Host has replied, guest can continue
    };

    // Check if user can send messages
    const canSendMessage = () => {
        if (isConversationClosed()) return false;
        if (isCurrentUserHost()) return true;
        return canGuestSendMessage();
    };

    // Get blocked message for guest
    const getBlockedMessage = () => {
        if (isConversationClosed()) {
            return "Esta conversación ha sido cerrada.";
        }
        if (isCurrentUserGuest() && !canGuestSendMessage()) {
            return "Debes esperar a que el anfitrión responda antes de enviar otro mensaje.";
        }
        return null;
    };

    // Función para seleccionar conversación (con lógica móvil)
    const handleSelectConversation = (conversation: Conversation) => {
        setSelectedConversation(conversation);
        if (isMobile) {
            setShowConversation(true);
        }
    };

    // Función para volver a la lista de conversaciones
    const handleBackToConversations = () => {
        setShowConversation(false);
        if (isMobile) {
            setSelectedConversation(null);
        }
    };

    const formatConversationForDisplay = (conv: Conversation) => {
        console.log(conv);
        
        const hostName = `${conv.host_firstname || ''} ${conv.host_lastname || ''}`.trim();
        const guestName = `${conv.guest_firstname || ''} ${conv.guest_lastname || ''}`.trim();
        const otherUser = currentUserId === conv.host_id ? guestName : hostName;
        const otherUserInitials = otherUser.split(' ').map(n => n[0]).join('').toUpperCase();
        const otherUserPhone = currentUserId === conv.host_id ? conv.guest_phone : conv.host_phone;

        return {
            ...conv,
            otherUser,
            otherUserInitials,
            otherUserPhone,
            lastMessage: messages.length > 0 ? messages[0] : null,
        };
    };

    // Función para formatear número de WhatsApp
    const formatWhatsAppNumber = (phone: string | undefined) => {
        if (!phone) return null;

        // Remover espacios, guiones y paréntesis
        let cleanPhone = phone.replace(/[\s\-\(\)]/g, '');

        // Si no tiene +54 al inicio, agregarlo
        if (!cleanPhone.startsWith('+54')) {
            // Si empieza con 54, agregar el +
            if (cleanPhone.startsWith('54')) {
                cleanPhone = '+' + cleanPhone;
            } else {
                // Si no, agregar +54
                cleanPhone = '+54' + cleanPhone;
            }
        }

        return cleanPhone;
    };

    const formatMessageForDisplay = (msg: Message) => {
        const senderName = `${msg.firstname || ''} ${msg.lastname || ''}`.trim();
        const isCurrentUser = msg.sender_id === currentUserId;

        return {
            ...msg,
            senderName,
            isCurrentUser,
        };
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case "confirmed":
                return <Badge className="bg-green-100 text-green-800">Confirmada</Badge>
            case "completed":
                return <Badge className="bg-blue-100 text-blue-800">Completada</Badge>
            case "cancelled":
                return <Badge className="bg-red-100 text-red-800">Cancelada</Badge>
            case "closed":
                return <Badge className="bg-gray-100 text-gray-800">Cerrada</Badge>
            case "active":
                return <Badge className="bg-green-100 text-green-800">Activa</Badge>
            default:
                return <Badge variant="secondary">{status || 'activa'}</Badge>
        }
    }

    return (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden ">
            <div className={`${isMobile ? 'flex flex-col h-[calc(100vh-2rem)]' : 'grid grid-cols-1 lg:grid-cols-3 h-[700px]'
                }`}>
                {/* Lista de Conversaciones */}
                <div className={`${isMobile ? 'flex-1' : 'lg:col-span-1'
                    } border-r flex flex-col overflow-y-auto ${isMobile && showConversation ? 'hidden' : ''
                    }`}>
                    <div className="p-4 border-b">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                                placeholder="Buscar conversaciones..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                            />
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto">
                        {conversationsLoading ? (
                            <div className="p-4 text-center">Cargando conversaciones...</div>
                        ) : filteredConversations.length === 0 ? (
                            <div className="p-4 text-center text-gray-500">No hay conversaciones</div>
                        ) : (
                            filteredConversations.map((conversation: Conversation) => {
                                const formattedConv = formatConversationForDisplay(conversation);
                                return (
                                    <div
                                        key={conversation.id}
                                        onClick={() => handleSelectConversation(conversation)}
                                        className={`p-4 border-b cursor-pointer hover:bg-gray-50 transition-colors ${selectedConversation?.id === conversation.id ? "bg-[#8B9456]/10 border-l-4 border-l-[#8B9456]" : ""
                                            }`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className="relative">
                                                <Avatar className="w-10 h-10">
                                                    <AvatarImage src="/placeholder.svg" alt={formattedConv.otherUser} />
                                                    <AvatarFallback>{formattedConv.otherUserInitials}</AvatarFallback>
                                                </Avatar>
                                            </div>

                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between mb-1">
                                                    <h4 className="font-medium text-gray-900 truncate">{formattedConv.otherUser}</h4>
                                                    <span className="text-xs text-gray-500">
                                                        {conversation.updated_at && format(new Date(conversation.updated_at), "HH:mm", { locale: es })}
                                                    </span>
                                                </div>

                                                <div className="flex items-center gap-2 mb-2">
                                                    <p className="text-sm text-gray-600">
                                                        {conversation.property_id ? `Propiedad #${conversation.property_id}` :
                                                            conversation.excursion_id ? `Excursión #${conversation.excursion_id}` : 'Reserva'}
                                                    </p>
                                                </div>

                                                {formattedConv.lastMessage && (
                                                    <p className="text-sm text-gray-700 line-clamp-1 mb-2">{formattedConv.lastMessage.content}</p>
                                                )}

                                                <div className="flex items-center gap-2">
                                                    {getStatusBadge(conversation.status || 'active')}
                                                    {conversation.start_date && conversation.end_date && (
                                                        <span className="text-xs text-gray-500">
                                                            {format(new Date(conversation.start_date), "dd/MM", { locale: es })} -
                                                            {format(new Date(conversation.end_date), "dd/MM", { locale: es })}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* Chat Interface */}
                <div className={`${isMobile ? 'flex-1' : 'lg:col-span-2'
                    } flex flex-col ${isMobile && !showConversation ? 'hidden' : ''
                    }`}>
                    {selectedConversation ? (
                        <>
                            {/* Header del Chat */}
                            <div className="p-4 border-b">
                                {(() => {
                                    const formattedConv = formatConversationForDisplay(selectedConversation);
                                    return (
                                        <>
                                            <div className="items-center gap-3">

                                                <div className="flex justify-between mb-2">
                                                    {/* Botón de volver (solo móvil) */}
                                                    {isMobile && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={handleBackToConversations}
                                                            className="p-2 hover:bg-gray-100 rounded-full"
                                                        >
                                                            <ArrowLeft className="w-5 h-5" />
                                                        </Button>
                                                    )}

                                                    <TooltipProvider>
                                                        <div className="flex items-center justify-between gap-2">
                                                            {/* Botón de aprobar reserva y enviar mail (solo para anfitriones cuando chat está cerrado y reserva NO aprobada) */}
                                                            {isCurrentUserHost() && isConversationClosed() && selectedConversation.booking_status !== 'approved' && (
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <div className="flex items-center gap-1">
                                                                            <Button
                                                                                variant="outline"
                                                                                size="sm"
                                                                                onClick={() => setShowEmailModal(true)}
                                                                                disabled={sendingEmail}
                                                                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                                                            >
                                                                                <Mail className="w-4 h-4 mr-1" />
                                                                                Aprobar fechas 
                                                                            </Button>
                                                                            <Info className="w-4 h-4 text-gray-400" />
                                                                        </div>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent className="max-w-xs">
                                                                        <p className="text-sm">
                                                                            Aprueba la reserva y envía un correo al cliente con el link de pago para confirmar la reserva
                                                                        </p>
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            )}

                                                            {/* Botón de cerrar chat (solo para anfitriones) */}
                                                            {isCurrentUserHost() && !isConversationClosed() && (
                                                                <>
                                                                    {/* Botón de aprobar reserva (solo si NO está aprobada) */}
                                                                    {selectedConversation.booking_status !== 'approved' && (
                                                                        <Tooltip>
                                                                            <TooltipTrigger asChild>
                                                                                <div className="flex items-center gap-1">
                                                                                    <Button
                                                                                        variant="outline"
                                                                                        size="sm"
                                                                                        onClick={() => setShowEmailModal(true)}
                                                                                        disabled={sendingEmail}
                                                                                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                                                                    >
                                                                                        <Mail className="w-4 h-4 mr-1" />
                                                                                        Aprobar reserva y enviar mail
                                                                                    </Button>
                                                                                    <Info className="w-4 h-4 text-gray-400" />
                                                                                </div>
                                                                            </TooltipTrigger>
                                                                            <TooltipContent className="max-w-xs">
                                                                                <p className="text-sm">
                                                                                    Aprueba la reserva y envía un correo al cliente con el link de pago para confirmar la reserva
                                                                                </p>
                                                                            </TooltipContent>
                                                                        </Tooltip>
                                                                    )}

                                                                    <Tooltip>
                                                                        <TooltipTrigger asChild>
                                                                            <div className="flex items-center gap-1">
                                                                                <Button
                                                                                    variant="outline"
                                                                                    size="sm"
                                                                                    onClick={handleCloseConversation}
                                                                                    disabled={closeConversationMutation.isPending}
                                                                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                                                >
                                                                                    {closeConversationMutation.isPending ? (
                                                                                        <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                                                                                    ) : (
                                                                                        <>
                                                                                            <X className="w-4 h-4 mr-1" />
                                                                                            Cerrar chat
                                                                                        </>
                                                                                    )}
                                                                                </Button>
                                                                                <Info className="w-4 h-4 text-gray-400" />
                                                                            </div>
                                                                        </TooltipTrigger>
                                                                        <TooltipContent className="max-w-xs">
                                                                            <p className="text-sm">
                                                                                Cierra la conversación. El cliente no podrá enviar más mensajes hasta que apruebes su reserva
                                                                            </p>
                                                                        </TooltipContent>
                                                                    </Tooltip>
                                                                </>
                                                            )}
                                                        </div>
                                                    </TooltipProvider>
                                                </div>

                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <Avatar className="w-10 h-10">
                                                            <AvatarImage src="/placeholder.svg" alt={formattedConv.otherUser} />
                                                            <AvatarFallback>{formattedConv.otherUserInitials}</AvatarFallback>
                                                        </Avatar>
                                                        <h3 className="font-semibold text-gray-900 text-nowrap">{formattedConv.otherUser}</h3>
                                                        {getStatusBadge(selectedConversation.status || 'active')}
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <p className="text-sm text-gray-600">
                                                            {selectedConversation.property_id ? `Propiedad #${selectedConversation.property_id}` :
                                                                selectedConversation.excursion_id ? `Excursión #${selectedConversation.excursion_id}` : 'Reserva'}
                                                        </p>
                                                        {selectedConversation.booking_status && getStatusBadge(selectedConversation.booking_status)}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Info de la Reserva */}
                                            {selectedConversation.start_date && selectedConversation.end_date && (
                                                <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                                                    <div className="flex items-center gap-4 text-sm text-gray-600">
                                                        <div className="flex items-center gap-1">
                                                            <Calendar className="w-4 h-4" />
                                                            <span>
                                                                {format(new Date(selectedConversation.start_date), "dd MMM", { locale: es })} -{" "}
                                                                {format(new Date(selectedConversation.end_date), "dd MMM yyyy", { locale: es })}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <MapPin className="w-4 h-4" />
                                                            <span>Reserva #{selectedConversation.booking_id}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Mensaje informativo cuando la reserva está aprobada (solo para guest) */}
                                            {isCurrentUserGuest() && selectedConversation.booking_status === 'approved' && (
                                                <div className="mt-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                                                    <div className="flex items-start gap-3">
                                                        <CheckCircle className="w-5 h-5 text-white flex-shrink-0 mt-0.5" />
                                                        <div className="flex-1">
                                                            <p className="text-sm font-medium text-white mb-1">
                                                                ¡Tu reserva está aprobada!
                                                            </p>
                                                            <p className="text-sm text-white">
                                                                Puedes recibir tus comprobantes desde la página{' '}
                                                                <a
                                                                    href="/mis-reservas"
                                                                    className="font-semibold underline hover:text-green-900"
                                                                >
                                                                    Mis Reservas
                                                                </a>
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Enlace de WhatsApp para el dueño/admin viendo mensajes del cliente */}
                                            {(() => {
                                                const formattedConv = formatConversationForDisplay(selectedConversation);
                                                const whatsappNumber = formatWhatsAppNumber(formattedConv.otherUserPhone);

                                                if (isCurrentUserHost() && whatsappNumber && selectedConversation.booking_status === 'approved') {
                                                    const propertyType = selectedConversation.property_id ? 'alojamiento' :
                                                                        selectedConversation.excursion_id ? 'excursión' : 'publicación';
                                                    const propertyId = selectedConversation.property_id || selectedConversation.excursion_id || '';

                                                    // Obtener el último mensaje del cliente
                                                    const guestMessages = messages.filter((msg: Message) => msg.sender_id === selectedConversation.guest_id);
                                                    const lastGuestMessage = guestMessages.length > 0 ? guestMessages[guestMessages.length - 1].content : '';

                                                    // Construir el mensaje de WhatsApp con referencia al mensaje del cliente
                                                    let whatsappMessage = `Hola ${formattedConv.otherUser}! Te escribo en referencia a tu consulta sobre ${propertyType === 'alojamiento' ? 'el' : 'la'} ${propertyType}${propertyId ? ` #${propertyId}` : ''}.`;

                                                    if (lastGuestMessage) {
                                                        whatsappMessage += `\n\nRecibí tu mensaje: "${lastGuestMessage}"`;
                                                    }

                                                    if (selectedConversation.start_date && selectedConversation.end_date) {
                                                        const startDate = format(new Date(selectedConversation.start_date), "dd/MM/yyyy", { locale: es });
                                                        const endDate = format(new Date(selectedConversation.end_date), "dd/MM/yyyy", { locale: es });
                                                        whatsappMessage += `\n\nFechas de reserva: ${startDate} - ${endDate}`;
                                                    }

                                                    whatsappMessage += `\n\n¿En qué puedo ayudarte?`;

                                                    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;

                                                    return (
                                                        <div className="mt-3">
                                                            <a
                                                                href={whatsappUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="inline-flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium transition-colors"
                                                            >
                                                                <MessageCircle className="w-4 h-4" />
                                                                Responder por WhatsApp al cliente
                                                            </a>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })()}
                                        </>
                                    );
                                })()}
                            </div>

                            {/* Mensajes */}
                            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                                {messagesLoading ? (
                                    <div className="text-center py-4">Cargando mensajes...</div>
                                ) : messages.length === 0 ? (
                                    <div className="text-center py-4 text-gray-500">No hay mensajes en esta conversación</div>
                                ) : (
                                    messages.reverse().map((message: Message) => {
                                        const formattedMsg = formatMessageForDisplay(message);
                                        return (
                                            <div
                                                key={message.id}
                                                className={`flex ${formattedMsg.isCurrentUser ? "justify-end" : "justify-start"}`}
                                            >
                                                <div
                                                    className={`max-w-[70%] p-3 rounded-lg ${formattedMsg.isCurrentUser ? "bg-[#8B9456] text-white" : "bg-gray-100 text-gray-900"
                                                        }`}
                                                >
                                                    <p className="text-sm">{message.content}</p>
                                                    <div className="flex items-center justify-between mt-1">
                                                        <p className={`text-xs ${formattedMsg.isCurrentUser ? "text-white/70" : "text-gray-500"}`}>
                                                            {formattedMsg.senderName}
                                                        </p>
                                                        <p className={`text-xs ${formattedMsg.isCurrentUser ? "text-white/70" : "text-gray-500"}`}>
                                                            {format(new Date(message.created_at), "HH:mm", { locale: es })}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>

                            {/* Input de Mensaje */}
                            <div className="p-4 border-t">
                                {/* Mensaje de bloqueo si aplica */}
                                {getBlockedMessage() && (
                                    <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                                        <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                                        <p className="text-sm text-amber-800">{getBlockedMessage()}</p>
                                    </div>
                                )}

                                <div className="flex gap-2">
                                    <Button variant="outline" size="icon" disabled>
                                        <Paperclip className="w-4 h-4" />
                                    </Button>
                                    <Textarea
                                        placeholder={canSendMessage() ? "Escribe tu mensaje..." : "No puedes enviar mensajes en este momento"}
                                        value={newMessage}
                                        onChange={(e) => setNewMessage(e.target.value)}
                                        className="flex-1 min-h-[40px] max-h-[120px] resize-none"
                                        disabled={sendMessageMutation.isPending || !canSendMessage()}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" && !e.shiftKey && canSendMessage()) {
                                                e.preventDefault()
                                                handleSendMessage()
                                            }
                                        }}
                                    />
                                    <Button
                                        onClick={handleSendMessage}
                                        className="bg-[#8B9456] hover:bg-[#7A8249]"
                                        disabled={sendMessageMutation.isPending || !newMessage.trim() || !canSendMessage()}
                                    >
                                        {sendMessageMutation.isPending ? (
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        ) : (
                                            <Send className="w-4 h-4" />
                                        )}
                                    </Button>
                                </div>

                                {/* Botón de WhatsApp para huéspedes cuando la reserva está aprobada */}
                                {isCurrentUserGuest() && selectedConversation.booking_status === 'approved' && (() => {
                                    const formattedConv = formatConversationForDisplay(selectedConversation);
                                    const whatsappNumber = formatWhatsAppNumber(formattedConv.otherUserPhone);

                                    if (whatsappNumber) {
                                        const propertyType = selectedConversation.property_id ? 'alojamiento' :
                                            selectedConversation.excursion_id ? 'excursión' : 'publicación';
                                        const propertyId = selectedConversation.property_id || selectedConversation.excursion_id || '';

                                        let whatsappMessage = `Hola ${formattedConv.otherUser}! Te escribo en referencia a mi reserva de ${propertyType === 'alojamiento' ? 'el' : 'la'} ${propertyType}${propertyId ? ` #${propertyId}` : ''}.`;

                                        if (selectedConversation.start_date && selectedConversation.end_date) {
                                            const startDate = format(new Date(selectedConversation.start_date), "dd/MM/yyyy", { locale: es });
                                            const endDate = format(new Date(selectedConversation.end_date), "dd/MM/yyyy", { locale: es });
                                            whatsappMessage += `\n\nFechas de mi reserva: ${startDate} - ${endDate}`;
                                        }

                                        whatsappMessage += `\n\nReserva #${selectedConversation.booking_id}`;

                                        const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;

                                        return (
                                            <div className="mt-3">
                                                <a
                                                    href={whatsappUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium transition-colors"
                                                >
                                                    <MessageCircle className="w-5 h-5" />
                                                    Seguir chateando con el anfitrión desde WhatsApp
                                                </a>
                                            </div>
                                        );
                                    }
                                    return null;
                                })()}
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 flex items-center justify-center">
                            <div className="text-center px-4">
                                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Search className="w-8 h-8 text-gray-400" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    Selecciona una conversación
                                </h3>
                                <p className="text-gray-600">
                                    {isMobile ? 'Toca una conversación de la lista para empezar a chatear' : 'Elige una conversación de la lista para comenzar a chatear'}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal para enviar correo */}
            {showEmailModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">Enviar correo de reserva</h3>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    setShowEmailModal(false);
                                    setMotivo("");
                                }}
                                disabled={sendingEmail}
                            >
                                <X className="w-4 h-4" />
                            </Button>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Motivo del correo *
                            </label>
                            <Textarea
                                placeholder="Ingresa el motivo para enviar el correo de reserva..."
                                value={motivo}
                                onChange={(e) => setMotivo(e.target.value)}
                                className="w-full min-h-[100px] resize-none"
                                disabled={sendingEmail}
                            />
                        </div>

                        <div className="flex justify-end gap-2">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setShowEmailModal(false);
                                    setMotivo("");
                                }}
                                disabled={sendingEmail}
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={handleSendReservationEmail}
                                disabled={sendingEmail || !motivo.trim()}
                                className="bg-blue-600 hover:bg-blue-700"
                            >
                                {sendingEmail ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                        Enviando...
                                    </>
                                ) : (
                                    <>
                                        <Mail className="w-4 h-4 mr-2" />
                                        Enviar correo
                                    </>
                                )}
                            </Button>
                        </div>

                        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                            <p className="text-xs text-gray-600">
                                <strong>Nota:</strong> Se enviará un correo al cliente con el motivo especificado
                                y las instrucciones para realizar el pago de la reserva.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
